import React from 'react';

import { HomeTestIds } from 'common/data/test-ids';
import { SectionHeader } from './section-header';
import {
  Tab,
  TabButton,
  TabButtonProps,
  TabIcon,
  TabIconProps,
  TabLink,
  TabLinkProps,
  TabList,
  TabLogo,
  TabLogoProps,
  TabSection,
  TabText,
} from './tab';

import styles from './navigation.less';

import { forTemplate } from 'common/lib/util/i18n';
const l = forTemplate('home');

type HomeTabProps = TabLinkProps;

const HomeTab: React.StatelessComponent<HomeTabProps> = (props) => (
  <Tab>
    <TabLink data-test-id={HomeTestIds.HomeLink} {...props}>
      <TabIcon iconName="home" />
      <TabText>{l('home')}</TabText>
    </TabLink>
  </Tab>
);

type BoardsTabProps = TabLinkProps;

const BoardsTab: React.StatelessComponent<BoardsTabProps> = (props) => (
  <Tab>
    <TabLink {...props}>
      <TabIcon iconName="board" />
      <TabText>{l('boards')}</TabText>
    </TabLink>
  </Tab>
);

interface TeamLinkSubTabProps extends TabLinkProps, TabIconProps {
  displayName: string;
  external?: boolean;
  selected: boolean;
}

const TeamLinkSubTab: React.StatelessComponent<TeamLinkSubTabProps> = ({
  displayName,
  external,
  iconName,
  selected,
  ...props
}) => (
  <Tab>
    <TabLink
      selected={selected}
      className={external ? styles.externalLink : ''}
      indented
      small
      quiet
      {...props}
    >
      <TabIcon iconName={iconName} />
      <TabText>{displayName}</TabText>
      {external && (
        <TabIcon iconName={'forward'} className={styles.externalIcon} />
      )}
    </TabLink>
  </Tab>
);

interface TeamTabProps extends TabLogoProps, TabLinkProps {
  displayName: string;
  expanded: boolean;
  orgId: string;
  linkTabs: TeamLinkSubTabProps[];
  selected: boolean;
}

const TeamTab: React.StatelessComponent<TeamTabProps> = ({
  displayName,
  expanded,
  orgId,
  linkTabs,
  logoUrl1x,
  logoUrl2x,
  selected,
  ...props
}) => (
  <Tab data-test-id={`${HomeTestIds.TeamTabSection}${orgId}`}>
    <TabLink selected={selected} {...props}>
      {logoUrl1x && <TabLogo logoUrl1x={logoUrl1x} logoUrl2x={logoUrl2x} />}
      {!logoUrl1x && <TabIcon iconName="organization" />}
      <TabText data-test-id={HomeTestIds.TeamTabName}>{displayName}</TabText>
    </TabLink>
    {expanded &&
      !!linkTabs.length && (
        <TabList>
          {linkTabs.map((linkTab) => <TeamLinkSubTab {...linkTab} />)}
        </TabList>
      )}
  </Tab>
);

type CreateTeamTabProps = TabButtonProps;

const CreateTeamTab: React.StatelessComponent<CreateTeamTabProps> = (props) => (
  <Tab>
    <TabButton
      quiet
      data-test-id={HomeTestIds.NavigationCreateTeamButton}
      {...props}
    >
      <TabIcon iconName="add" />
      <TabText>{l('home-create-team')}</TabText>
    </TabButton>
  </Tab>
);

interface NavigationProps {
  homeTab: HomeTabProps;
  boardsTab: BoardsTabProps;
  teamTabs: TeamTabProps[];
  createTeamTab: CreateTeamTabProps;
}

export const Navigation: React.StatelessComponent<NavigationProps> = ({
  homeTab,
  boardsTab,
  teamTabs,
  createTeamTab,
  ...props
}) => (
  <nav {...props}>
    <TabSection>
      <TabList>
        <BoardsTab {...boardsTab} />
        <HomeTab {...homeTab} />
      </TabList>
    </TabSection>
    <TabSection>
      <SectionHeader noMargin>{l('teams')}</SectionHeader>
      <TabList>
        {teamTabs.map((teamTab) => <TeamTab {...teamTab} />)}
        <CreateTeamTab {...createTeamTab} />
      </TabList>
    </TabSection>
  </nav>
);



// WEBPACK FOOTER //
// ./app/scripts/views/home/presentational/navigation.tsx